import React, { useState, useCallback } from "react";
import Layout from "../../../components/layout/layout";
import { TabGroup } from "../../../components/tabs/Tabs";
import { SearchHeader } from "../../../components/seachHeader/SearchHeader";
import {Pencil,Star,Trash,Plus,Search} from "lucide-react";
import tooltipDataNotFoundImg from "../../../assets/image/tooltipDataNotFoundImg.png";
// Firebase imports
import { collection, query, where, onSnapshot, getDocs, getDoc, doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import DeleteModelSimple from "../../../components/DeleteModelSimple";
import { FaSortDown, FaSortUp } from "react-icons/fa";

// Data interfaces
interface TooltipPackage {
  packageId: string;
  packageName: string;
  created: string;
  createdAtRaw?: number;
  lastSale: string;
  ratings: number;
  sales: number;
  earnings: string;
  status: string; // "draft" or "published"
  price: number;
  currency: string;
  primaryUrl: string;
  tooltips: unknown[];
  description?: string;
  creatorId: string;
  expertName?: string;
  docId: string;
  admindisabled?: boolean;
}

interface TooltipSalesData {
  packageId: string;
  packageName: string;
  customerName: string;
  purchased: string;
  purchasedAt?: string | number | Date;
  ratings: number;
  reviews: string;
  earnings: string;
  price: number;
  orgName: string;
  primaryUrl: string;
  expertName?: string;
}

interface TooltipPurchase {
  price?: number | string;
  rating?: number | string;
  purchasedAt?: unknown;
  purchaserId?: string;
  review?: string;
  packageId?: string;
  description?: string;
  purchaserName?: string;
  orgName?: string;
}

// Utility function to parse Firestore Timestamp
const parseFirestoreTimestamp = (value: unknown): Date | null => {
  if (value === null || value === undefined) return null;

  if (typeof value === 'object') {
    const v = value as { toDate?: () => Date; seconds?: number; nanoseconds?: number };

    if (typeof v.toDate === 'function') {
      return v.toDate();
    }

    if (typeof v.seconds === 'number') {
      return new Date(v.seconds * 1000 + (v.nanoseconds || 0) / 1000000);
    }
  }

  if (typeof value === 'number') {
    if (value < 1e12) {
      return new Date(value * 1000);
    }
    return new Date(value);
  }

  if (typeof value === 'string') {
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      return d;
    }
  }

  return null;
};

// Utility function to parse date strings in MM/DD/YYYY or YYYY-MM-DD format
const parseDateString = (dateString: string): Date | null => {
  if (!dateString || dateString === 'N/A') return null;

  // Try parsing as YYYY-MM-DD first (from date inputs)
  const yyyyMmDdMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (yyyyMmDdMatch) {
    const [, year, month, day] = yyyyMmDdMatch;
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // Try parsing as MM/DD/YYYY (from stored data)
  const mmDdYyyyMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmDdYyyyMatch) {
    const [, month, day, year] = mmDdYyyyMatch;
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // Fallback to standard Date parsing
  const d = new Date(dateString);
  return !isNaN(d.getTime()) ? d : null;
};

// Batch fetch profiles for userIds
const batchFetchProfiles = async (userIds: string[]) => {
  if (userIds.length === 0) return new Map<string, any>();

  const uniqueIds = [...new Set(userIds)];
  const batches: string[][] = [];

  for (let i = 0; i < uniqueIds.length; i += 10) {
    batches.push(uniqueIds.slice(i, i + 10));
  }

  const profilesMap = new Map<string, any>();

  await Promise.all(
    batches.map(async (batch) => {
      const profileQuery = query(collection(db, 'profiles'), where('userId', 'in', batch));
      const profileSnap = await getDocs(profileQuery);

      profileSnap.docs.forEach((doc) => {
        const data = doc.data();
        profilesMap.set(data.userId, data);
      });
    })
  );

  return profilesMap;
};

// Batch fetch packages for packageIds
const batchFetchPackages = async (packageIds: string[]) => {
  if (packageIds.length === 0) return new Map<string, any>();

  const uniqueIds = [...new Set(packageIds)];
  const batches: string[][] = [];
  
  for (let i = 0; i < uniqueIds.length; i += 10) {
    batches.push(uniqueIds.slice(i, i + 10));
  }

  const packagesMap = new Map<string, any>();

  await Promise.all(
    batches.map(async (batch) => {
      const packageQuery = query(collection(db, 'tooltipPackages'), where('packageId', 'in', batch));
      const packageSnap = await getDocs(packageQuery);

      packageSnap.docs.forEach((doc) => {
        const data = doc.data();
        packagesMap.set(data.packageId, data);
      });
    })
  );

  return packagesMap;
};

// Batch fetch users for userIds
const batchFetchUsers = async (userIds: string[]) => {
  if (userIds.length === 0) return new Map<string, any>();

  const usersMap = new Map<string, any>();

  await Promise.all(
    userIds.map(async (userId) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          usersMap.set(userId, userDoc.data());
        }
      } catch (error) {
        console.error(`Error fetching user ${userId}:`, error);
      }
    })
  );

  return usersMap;
};

// Batch fetch organizations for orgIds
const batchFetchOrganizations = async (orgIds: string[]) => {
  if (orgIds.length === 0) return new Map<string, any>();

  const uniqueIds = [...new Set(orgIds)];
  const organizationsMap = new Map<string, any>();

  await Promise.all(
    uniqueIds.map(async (orgId) => {
      try {
        const orgDoc = await getDoc(doc(db, 'organizations', orgId));
        if (orgDoc.exists()) {
          organizationsMap.set(orgId, orgDoc.data());
        }
      } catch (error) {
        console.error(`Error fetching organization ${orgId}:`, error);
      }
    })
  );

  return organizationsMap;
};// Optimized: Fetch all purchase data for packages in ONE query
const getPurchaseDataForPackages = async (packageIds: string[]) => {
  if (packageIds.length === 0) {
    return new Map();
  }

  try {
    const purchasesRef = collection(db, 'tooltipPurchases');
    
    // Firestore 'in' query supports up to 10 items, so we need to batch
    const batchSize = 10;
    const batches: string[][] = [];
    
    for (let i = 0; i < packageIds.length; i += batchSize) {
      batches.push(packageIds.slice(i, i + batchSize));
    }

    const allPurchases: Map<string, TooltipPurchase[]> = new Map();
    
    // Fetch all batches in parallel
    await Promise.all(
      batches.map(async (batch) => {
        const purchaseQuery = query(
          purchasesRef,
          where('packageId', 'in', batch)
        );
        
        const purchaseSnapshot = await getDocs(purchaseQuery);
        
        purchaseSnapshot.docs.forEach((doc) => {
          const data = doc.data() as TooltipPurchase;
          const packageId = data.packageId || '';
          if (!allPurchases.has(packageId)) {
            allPurchases.set(packageId, []);
          }
          allPurchases.get(packageId)!.push(data);
        });
      })
    );

    return allPurchases;
  } catch (error) {
    console.error('Error fetching purchase data:', error);
    return new Map();
  }
};

// Compute package statistics from purchases
const computePackageStats = (purchases: TooltipPurchase[]) => {
  if (purchases.length === 0) {
    return {
      lastSale: 'N/A',
      salesCount: 0,
      averageRating: 0,
      totalEarnings: 0
    };
  }

  let latestDate: Date | null = null;
  let totalRating = 0;
  let ratingCount = 0;
  let totalEarnings = 0;

  purchases.forEach((purchase) => {
    // Latest purchase date
    const purchaseDate = parseFirestoreTimestamp(purchase.purchasedAt);
    if (purchaseDate && (!latestDate || purchaseDate > latestDate)) {
      latestDate = purchaseDate;
    }

    // Ratings
    const rating = purchase.rating;
    const ratingNum = typeof rating === 'number' ? rating : (rating ? Number(rating) : NaN);
    if (!isNaN(ratingNum)) {
      totalRating += ratingNum;
      ratingCount += 1;
    }

    // Earnings
    const priceVal = purchase.price;
    const priceNum = typeof priceVal === 'number' ? priceVal : (priceVal ? Number(priceVal) : NaN);
    if (!isNaN(priceNum)) {
      totalEarnings += priceNum;
    }
  });

  return {
    lastSale: latestDate
      ? latestDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
      : 'N/A',
    salesCount: purchases.length,
    averageRating: ratingCount > 0 ? Math.round((totalRating / ratingCount) * 10) / 10 : 0,
    totalEarnings
  };
};

const Tooltip = () => {
  const [activeTab, setActiveTab] = useState("package");
  // Separate pagination state for each tab
  const [packagePage, setPackagePage] = useState(1);
  const [salesPage, setSalesPage] = useState(1);
  const itemsPerPage = 10;
  const [formData, setFormData] = useState({
    packageName: '',
    primaryUrl: '',
    price: ''
  });
  // Add package selection state
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);

  // Sales filter states
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedRating, setSelectedRating] = useState("");
  const [uniqueOrgs, setUniqueOrgs] = useState<{ value: string; label: string }[]>([]);

  // Real data state
  const [tooltipPackages, setTooltipPackages] = useState<TooltipPackage[]>([]);
  console.log(tooltipPackages,"tooltipPackages")
  const [tooltipSalesData, setTooltipSalesData] = useState<TooltipSalesData[]>([]);
  const [fetchingPackages, setFetchingPackages] = useState(false);
  const [fetchingSales, setFetchingSales] = useState(false);
  const [error, setError] = useState<string | null>(null);
console.log(tooltipSalesData,"tooltipSalesData")
  // Applied filters state for package tooltip filters
  const [appliedFilters, setAppliedFilters] = useState<any>(null);
  // Search query state
  const [searchQuery, setSearchQuery] = useState("");
  // Status toggle modal state
  const [statusToggleModal, setStatusToggleModal] = useState<{ open: boolean; action: string; package: TooltipPackage | null }>({ open: false, action: '', package: null });
  const [statusToggleLoading, setStatusToggleLoading] = useState(false);
  // Sorting state for package and sales tables
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: string, direction: 'asc' | 'desc') => {
    setSortColumn(column);
    setSortDirection(direction);
  };
  // Real-time listener for tooltip packages using onSnapshot
  const setupPackagesListener = useCallback(() => {
    setFetchingPackages(true);
    
    const q = query(collection(db, 'tooltipPackages'));
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      try {
        console.log(`Fetched ${querySnapshot.docs.length} tooltip packages`);
        const packages: TooltipPackage[] = [];
        const creatorIds: string[] = [];
        
        querySnapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const createdDate = parseFirestoreTimestamp(data.createdAt);
          
          const packageData: TooltipPackage = {
            packageId: data.packageId || docSnap.id,
            packageName: data.name || data.packageName || '',
            created: createdDate ? createdDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }) : 'N/A',
            createdAtRaw: createdDate ? createdDate.getTime() : undefined,
            lastSale: 'N/A', // Will be computed
            ratings: 0, // Will be computed
            sales: 0, // Will be computed
            earnings: '0', // Will be computed
            status: data.status || (data.admindisabled ? "draft" : "published"),
            price: typeof data.price === 'number' ? data.price : parseFloat(data.price) || 0,
            currency: data.currency || 'USD',
            primaryUrl: data.primaryUrl || '',
            tooltips: data.tooltips || [],
            description: data.description || '',
            creatorId: data.creatorId || '',
            expertName: 'Unknown Expert', // Will be set after batch fetch
            docId: docSnap.id,
            admindisabled: data.admindisabled || false
          };
          
          packages.push(packageData);
          if (data.creatorId) {
            creatorIds.push(data.creatorId);
          }
        });
        
        // Batch fetch profiles
        const profilesMap = await batchFetchProfiles(creatorIds);
        
        // Set expert names
        packages.forEach(pkg => {
          if (pkg.creatorId && profilesMap.has(pkg.creatorId)) {
            const profile = profilesMap.get(pkg.creatorId);
            pkg.expertName = profile.name || profile.email || 'Unknown Expert';
          }
        });
        
        // Get purchase data for all packages
        const packageIds = packages.map(p => p.packageId);
        const purchasesMap = await getPurchaseDataForPackages(packageIds);
        
        // Compute stats for each package
        packages.forEach(pkg => {
          const purchases = purchasesMap.get(pkg.packageId) || [];
          const stats = computePackageStats(purchases);
          pkg.lastSale = stats.lastSale;
          pkg.ratings = stats.averageRating;
          pkg.sales = stats.salesCount;
          pkg.earnings = `${stats.totalEarnings.toFixed(2)}`;
        });
        
        // Sort packages from last to first by created date
        packages.sort((a, b) => {
          const dateA = a.createdAtRaw || 0;
          const dateB = b.createdAtRaw || 0;
          return dateB - dateA; // descending order (most recent first)
        });
        
        console.log(`Processed ${packages.length} tooltip packages`);
        setTooltipPackages(packages);
        setFetchingPackages(false);
      } catch (error) {
        console.error('Error in packages listener:', error);
        setError('Failed to load tooltip packages');
        setFetchingPackages(false);
      }
    }, (error) => {
      console.error('Error in packages listener:', error);
      setError('Failed to load tooltip packages');
      setFetchingPackages(false);
    });
    
    return unsubscribe;
  }, []);

  // Real-time listener for tooltip sales data using onSnapshot
  const setupSalesListener = useCallback(() => {
    setFetchingSales(true);
    
    const q = query(collection(db, 'tooltipPurchases'));
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      try {
        const sales: TooltipSalesData[] = [];
        const purchaserIds: string[] = [];
        const packageIds: string[] = [];
        
        querySnapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const purchasedDate = parseFirestoreTimestamp(data.purchasedAt);
          
          const price = typeof data.price === 'number' ? data.price : parseFloat(data.price) || 0;
          const rating = typeof data.rating === 'number' ? data.rating : (data.rating ? parseFloat(data.rating) : 0);
          
          sales.push({
            packageId: data.packageId || '',
            packageName: 'Unknown Package', // Will be set after batch fetch
            customerName: 'Unknown Customer', // Will be set after batch fetch
            purchased: purchasedDate ? purchasedDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }) : 'N/A',
            purchasedAt: purchasedDate || undefined,
            ratings: rating,
            reviews: data.review || '',
            earnings: `${price.toFixed(2)}`,
            price,
            orgName: 'Unknown Organization', // Will be set after batch fetch
            primaryUrl: '', // Will be set after batch fetch
            expertName: 'Unknown Expert' // Will be set after batch fetch
          });
          
          if (data.purchaserId) purchaserIds.push(data.purchaserId);
          if (data.packageId) packageIds.push(data.packageId);
        });
        
        // Batch fetch packages first
        const packagesMap = await batchFetchPackages(packageIds);
        
        // Collect creatorIds from packages
        const creatorIds: string[] = [];
        packagesMap.forEach((pkg) => {
          if (pkg.creatorId) creatorIds.push(pkg.creatorId);
        });
        
        // Combine all userIds
        const allUserIds = [...new Set([...purchaserIds, ...creatorIds])];
        
        // Batch fetch all profiles
        const profilesMap = await batchFetchProfiles(allUserIds);
        
        // Batch fetch users to get orgId
        const usersMap = await batchFetchUsers(purchaserIds);
        
        // Collect orgIds from users
        const orgIds: string[] = [];
        usersMap.forEach((userData) => {
          if (userData.orgId) {
            orgIds.push(userData.orgId);
          }
        });
        
        // Batch fetch organizations
        const organizationsMap = await batchFetchOrganizations(orgIds);
        
        // Set details from maps
        sales.forEach((sale, index) => {
          const data = querySnapshot.docs[index].data();
          
          // Set purchaser details
          if (data.purchaserId && profilesMap.has(data.purchaserId)) {
            const profile = profilesMap.get(data.purchaserId);
            sale.customerName = profile.name || profile.email || 'Unknown Customer';
            
            // Get organization name from users and organizations collections
            const userData = usersMap.get(data.purchaserId);
            if (userData && userData.orgId && organizationsMap.has(userData.orgId)) {
              const orgData = organizationsMap.get(userData.orgId);
              sale.orgName = orgData.name || 'Unknown Organization';
            } else {
              sale.orgName = 'Unknown Organization';
            }
          }
          
          // Set package details
          if (data.packageId && packagesMap.has(data.packageId)) {
            const pkg = packagesMap.get(data.packageId);
            sale.packageName = pkg.name || pkg.packageName || 'Unknown Package';
            sale.primaryUrl = pkg.primaryUrl || '';
            
            // Get expert name
            if (pkg.creatorId && profilesMap.has(pkg.creatorId)) {
              const profile = profilesMap.get(pkg.creatorId);
              sale.expertName = profile.name || profile.email || 'Unknown Expert';
            }
          }
        });
        
        // Sort sales from last to first by purchased date
        sales.sort((a, b) => {
          const dateA = a.purchasedAt ? new Date(a.purchasedAt).getTime() : 0;
          const dateB = b.purchasedAt ? new Date(b.purchasedAt).getTime() : 0;
          return dateB - dateA; // descending order (most recent first)
        });
        
        // Collect unique organizations for filter dropdown
        const orgSet = new Set<string>();
        sales.forEach(sale => {
          if (sale.orgName && sale.orgName !== 'Unknown Organization') {
            orgSet.add(sale.orgName);
          }
        });
        const uniqueOrgArray = Array.from(orgSet).map(org => ({ value: org, label: org })).sort((a, b) => a.label.localeCompare(b.label));
        setUniqueOrgs(uniqueOrgArray);
        
        setTooltipSalesData(sales);
        setFetchingSales(false);
      } catch (error) {
        console.error('Error in sales listener:', error);
        setError('Failed to load sales data');
        setFetchingSales(false);
      }
    }, (error) => {
      console.error('Error in sales listener:', error);
      setError('Failed to load sales data');
      setFetchingSales(false);
    });
    
    return unsubscribe;
  }, []);



  const tabs = [
    { label: "Package Summary", value: "package" },
    { label: "Sales History", value: "sales" },
  ];

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    // Reset pagination when search changes
    setPackagePage(1);
    setSalesPage(1);
  };


  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };



  // Add package change handler
  const handlePackageChange = (packages: string[]) => {
    setSelectedPackages(packages);
    // Reset sales pagination when filter changes
    setSalesPage(1);
  };

  // Sales filter handlers
  const handleOrganizationChange = (orgs: string[]) => {
    setSelectedOrganizations(orgs || []);
    setSalesPage(1);
  };

  const handleDateRangeChange = (range: { from: string; to: string }) => {
    setDateFrom(range.from);
    setDateTo(range.to);
    setSalesPage(1);
  };

  const handleRatingChange = (rating: string) => {
    setSelectedRating(rating);
    setSalesPage(1);
  };

  // Select data and pagination state based on activeTab
  // Apply filters to packages
  const filteredPackages = React.useMemo(() => {
    // First apply search filtering
    const lowerQuery = searchQuery.trim().toLowerCase();
    let filtered = tooltipPackages.filter((p) => {
      if (!lowerQuery) return true;
      
      // Search in package name
      if (p.packageName.toLowerCase().includes(lowerQuery)) return true;
      
     
      // Search in primary URL
      if (p.primaryUrl.toLowerCase().includes(lowerQuery)) return true;
      
      // Search in expert name
      if (p.expertName && p.expertName.toLowerCase().includes(lowerQuery)) return true;
      
      return false;
    });

    // Then apply appliedFilters
    if (appliedFilters) {
      // Created date range
      if (appliedFilters.createdDateRange) {
        const from = appliedFilters.createdDateRange.from ? parseDateString(appliedFilters.createdDateRange.from) : null;
        const to = appliedFilters.createdDateRange.to ? parseDateString(appliedFilters.createdDateRange.to) : null;
        filtered = filtered.filter(pkg => {
          const createdDate = parseDateString(pkg.created);
          if (!createdDate) return false;
          if (from && createdDate < from) return false;
          if (to && createdDate > to) return false;
          return true;
        });
      }

      // Last sale date range
      if (appliedFilters.lastSaleDateRange) {
        const from = appliedFilters.lastSaleDateRange.from ? parseDateString(appliedFilters.lastSaleDateRange.from) : null;
        const to = appliedFilters.lastSaleDateRange.to ? parseDateString(appliedFilters.lastSaleDateRange.to) : null;
        filtered = filtered.filter(pkg => {
          const lastSaleDate = parseDateString(pkg.lastSale);
          if (!lastSaleDate) return false;
          if (from && lastSaleDate < from) return false;
          if (to && lastSaleDate > to) return false;
          return true;
        });
      }

      // Package tooltip rating
      if (appliedFilters.packageTooltipRating) {
        const rating = parseFloat(appliedFilters.packageTooltipRating);
        filtered = filtered.filter(pkg => pkg.ratings >= rating);
      }

      // Sales ranges
      if (appliedFilters.salesRanges) {
        filtered = filtered.filter(pkg => {
          const sales = pkg.sales;
          if (appliedFilters.salesRanges.range1 && !(sales >= 0 && sales <= 10)) return false;
          if (appliedFilters.salesRanges.range2 && !(sales >= 11 && sales <= 20)) return false;
          if (appliedFilters.salesRanges.range3 && !(sales >= 21 && sales <= 50)) return false;
          if (appliedFilters.salesRanges.range4 && !(sales >= 51 && sales <= 100)) return false;
          if (appliedFilters.salesRanges.range5 && sales < 200) return false;
          if (appliedFilters.salesRanges.custom) {
            const min = appliedFilters.salesRanges.customMin;
            const max = appliedFilters.salesRanges.customMax;
            if (min !== null && sales < min) return false;
            if (max !== null && sales > max) return false;
          }
          return true;
        });
      }

      // Earnings ranges
      if (appliedFilters.earningsRanges) {
        filtered = filtered.filter(pkg => {
          const earnings = parseFloat(pkg.earnings.replace('$', ''));
          if (appliedFilters.earningsRanges.range1 && !(earnings >= 0 && earnings <= 100)) return false;
          if (appliedFilters.earningsRanges.range2 && !(earnings >= 101 && earnings <= 500)) return false;
          if (appliedFilters.earningsRanges.range3 && !(earnings >= 501 && earnings <= 1000)) return false;
          if (appliedFilters.earningsRanges.range4 && !(earnings >= 1001 && earnings <= 5000)) return false;
          if (appliedFilters.earningsRanges.range5 && !(earnings >= 5001 && earnings <= 10000)) return false;
          if (appliedFilters.earningsRanges.range6 && earnings > 10000) return false;
          if (appliedFilters.earningsRanges.custom) {
            const min = appliedFilters.earningsRanges.customMin;
            const max = appliedFilters.earningsRanges.customMax;
            if (min !== null && earnings < min) return false;
            if (max !== null && earnings > max) return false;
          }
          return true;
        });
      }
    }

    return filtered;
  }, [tooltipPackages, searchQuery, appliedFilters]);

  // Apply search to sales data
  const filteredSales = React.useMemo(() => {
    const lowerQuery = searchQuery.trim().toLowerCase();
    let filtered = tooltipSalesData.filter((s) => {
      if (!lowerQuery) return true;
      
      // Search in customer name
      if (s.customerName.toLowerCase().includes(lowerQuery)) return true;
      
      // Search in package name
      if (s.packageName.toLowerCase().includes(lowerQuery)) return true;
      
      // Search in org name
      if (s.orgName && s.orgName.toLowerCase().includes(lowerQuery)) return true;
      
      // Search in primary URL
      if (s.primaryUrl && s.primaryUrl.toLowerCase().includes(lowerQuery)) return true;
      
      // Search in expert name
      if (s.expertName && s.expertName.toLowerCase().includes(lowerQuery)) return true;
      
      return false;
    });

    // Apply organization filter
    if (selectedOrganizations.length > 0) {
      filtered = filtered.filter((s) => selectedOrganizations.includes(s.orgName));
    }

    // Apply date range filter
    if (dateFrom || dateTo) {
      filtered = filtered.filter((s) => {
        const purchaseDate = parseDateString(s.purchased);
        if (!purchaseDate) return false;
        
        // Normalize dates to compare only date parts (ignore time)
        const purchaseDateOnly = new Date(purchaseDate.getFullYear(), purchaseDate.getMonth(), purchaseDate.getDate());
        
        if (dateFrom) {
          const fromDate = new Date(dateFrom + 'T00:00:00');
          const fromDateOnly = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
          if (purchaseDateOnly < fromDateOnly) return false;
        }
        if (dateTo) {
          const toDate = new Date(dateTo + 'T23:59:59');
          const toDateOnly = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
          if (purchaseDateOnly > toDateOnly) return false;
        }
        return true;
      });
    }

    // Apply rating filter
    if (selectedRating) {
      filtered = filtered.filter((s) => {
        const rating = s.ratings;
        if (selectedRating === '1' && rating < 1) return false;
        if (selectedRating === '2' && (rating < 1 || rating > 2)) return false;
        if (selectedRating === '3' && (rating < 2 || rating > 3)) return false;
        if (selectedRating === '4' && (rating < 3 || rating > 4)) return false;
        if (selectedRating === '5' && rating < 4) return false;
        return true;
      });
    }

    return filtered;
  }, [tooltipSalesData, searchQuery, selectedOrganizations, dateFrom, dateTo, selectedRating]);

  // Apply sorting to packages if requested (sales, earnings, or name)
  // Memoized sorted packages
  const sortedPackages = React.useMemo(() => {
    const sorted = [...filteredPackages];
    if (sortColumn) {
      sorted.sort((a, b) => {
        // Name sorting (string) — use localeCompare for correct alphabetical order
        if (sortColumn === 'name') {
          const aName = (a.packageName || '').toString().toLowerCase();
          const bName = (b.packageName || '').toString().toLowerCase();
          const cmp = aName.localeCompare(bName);
          return sortDirection === 'asc' ? cmp : -cmp;
        }

        // Expert name sorting
        if (sortColumn === 'expertName') {
          const aName = (a.expertName || '').toString().toLowerCase();
          const bName = (b.expertName || '').toString().toLowerCase();
          const cmp = aName.localeCompare(bName);
          return sortDirection === 'asc' ? cmp : -cmp;
        }

        let aVal = 0;
        let bVal = 0;
        if (sortColumn === 'sales') {
          aVal = a.sales || 0;
          bVal = b.sales || 0;
        } else if (sortColumn === 'earnings') {
          aVal = parseFloat(String(a.earnings || '0').replace('$', '')) || 0;
          bVal = parseFloat(String(b.earnings || '0').replace('$', '')) || 0;
        } else {
          return 0;
        }
        if (sortDirection === 'asc') {
          return aVal - bVal;
        } else {
          return bVal - aVal;
        }
      });
    }
    return sorted;
  }, [filteredPackages, sortColumn, sortDirection]);

  // Apply sorting to sales data if requested (earnings or name)
  const sortedSales = React.useMemo(() => {
    const sorted: TooltipSalesData[] = [...filteredSales];
    if (sortColumn) {
      if (sortColumn === 'earnings') {
        sorted.sort((a, b) => {
          const aVal = parseFloat(String(a.earnings || '0').replace('$', '')) || 0;
          const bVal = parseFloat(String(b.earnings || '0').replace('$', '')) || 0;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        });
      } else if (sortColumn === 'name') {
        sorted.sort((a, b) => {
          const aName = (a.packageName || '').toString().toLowerCase();
          const bName = (b.packageName || '').toString().toLowerCase();
          const cmp = aName.localeCompare(bName);
          return sortDirection === 'asc' ? cmp : -cmp;
        });
      } else if (sortColumn === 'expertName') {
        sorted.sort((a, b) => {
          const aName = (a.expertName || '').toString().toLowerCase();
          const bName = (b.expertName || '').toString().toLowerCase();
          const cmp = aName.localeCompare(bName);
          return sortDirection === 'asc' ? cmp : -cmp;
        });
      }
    }
    return sorted;
  }, [filteredSales, sortColumn, sortDirection]);

  const filterDate = activeTab === "package" 
    ? sortedPackages 
    : selectedPackages.length === 0 
      ? sortedSales 
      : sortedSales.filter(sale => selectedPackages.includes(sale.packageId));
  const currentPage = activeTab === "package" ? packagePage : salesPage;
  const setCurrentPage = activeTab === "package" ? setPackagePage : setSalesPage;

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filterDate.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filterDate.length / itemsPerPage);

  // Handle page change for the current tab
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // When switching tabs, reset the corresponding page to 1
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === "package") setPackagePage(1);
    if (tab === "sales") {
      setSalesPage(1);
      setSelectedPackages([]);
    }
    // Reset sorting when switching tabs
    setSortColumn(null);
    setSortDirection('asc');
  };

  // Set up real-time listeners
  React.useEffect(() => {
    let packagesUnsubscribe: (() => void) | null = null;
    let salesUnsubscribe: (() => void) | null = null;

    const setupListeners = async () => {
      packagesUnsubscribe = setupPackagesListener();
      salesUnsubscribe = setupSalesListener();
    };

    setupListeners();

    return () => {
      if (packagesUnsubscribe) packagesUnsubscribe();
      if (salesUnsubscribe) salesUnsubscribe();
    };
  }, [setupPackagesListener, setupSalesListener]);

  // Reset package page when applied filters change
  React.useEffect(() => {
    setPackagePage(1);
  }, [appliedFilters]);

  // Reset pages when search query changes
  React.useEffect(() => {
    setPackagePage(1);
    setSalesPage(1);
  }, [searchQuery]);

  // Status toggle handlers
  const handleStatusToggleConfirm = async () => {
    const { action, package: pkg } = statusToggleModal;
    if (!pkg) return;

    setStatusToggleLoading(true);
    const newAdmindisabled = action === 'disable';
    const newStatus = newAdmindisabled ? 'draft' : 'published';

    try {
      await updateDoc(doc(db, 'tooltipPackages', pkg.docId), { 
        status: newStatus,
        admindisabled: newAdmindisabled
      });
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setStatusToggleLoading(false);
      setStatusToggleModal({ open: false, action: '', package: null });
    }
  };

  const handleStatusToggleCancel = () => {
    setStatusToggleModal({ open: false, action: '', package: null });
  };

  return (
    <Layout>
      <div
        className="p-6 mx-auto bg-white rounded-lg shadow-sm"
        style={{
          height: "calc(100vh - 112px)",
          overflowY: "hidden",
          overflowX: "hidden"
        }}
      >
        <div className="flex justify-between items-center mb-1">
          <TabGroup
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
         
        </div>
        <SearchHeader
          title={activeTab === "package" ? "Tooltip Packages" : "Sales"}
          onSearch={handleSearch}
          onFilter={setAppliedFilters}
          appliedFilters={appliedFilters}
          hasTooltipSelectAdmin={activeTab === "sales"}
          packageOptions={tooltipPackages.map(p => ({ value: p.packageId, label: p.packageName }))}
          selectedPackages={selectedPackages}
          onPackageChange={handlePackageChange}
            hasPackageTooltipFilters={activeTab === "package"}
             hasSalesFilters={activeTab === "sales"}
          organizationOptions={uniqueOrgs}
          selectedOrganizations={selectedOrganizations}
          onOrganizationChange={handleOrganizationChange}
          onDateRangeChange={handleDateRangeChange}
          onRatingChange={handleRatingChange}
        />

        <div
          className="bg-white rounded-[12px] border-[1px] border-[#EBEBEB] overflow-hidden overflow-x-auto"
          style={{
            height: "calc(80vh - 150px)",
            overflowY: "auto",
            overflowX: "hidden"
          }}
        >
          <table className="min-w-full divide-y max-h-[700px] divide-gray-200">
            <thead className="bg-[#FEF0EA]">
              <tr>
                  {activeTab === "package" && (
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                  Status
                </th>)}
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                  Package ID
                </th>
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                 <div className="flex items-center gap-2">
                
                                  <span>
                                  Package Name
                                  </span>
                                  <span className="col-span-1 flex flex-col">
                
                                      <button onClick={() => handleSort('name', 'asc')}>
                                        <FaSortUp className={`w-3 h-3 ${sortColumn === 'name' && sortDirection === 'asc' ? 'text-[#f15d18]' : 'text-gray-500'}`} />
                                      </button>
                                      <button onClick={() => handleSort('name', 'desc')}>
                                        <FaSortDown className={`w-3 h-3 ${sortColumn === 'name' && sortDirection === 'desc' ? 'text-[#f15d18]' : 'text-gray-500'}`} />   </button>
                                      </span>
                                  </div>
                </th>
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                  Package URL
                </th>
              
                  <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                 
                  <div className="flex items-center gap-2">
                
                                  <span>
                                  Expert Name
                                  </span>
                                  <span className="col-span-1 flex flex-col">
                
                                      <button onClick={() => handleSort('expertName', 'asc')}>
                                        <FaSortUp className={`w-3 h-3 ${sortColumn === 'expertName' && sortDirection === 'asc' ? 'text-[#f15d18]' : 'text-gray-500'}`} />
                                      </button>
                                      <button onClick={() => handleSort('expertName', 'desc')}>
                                        <FaSortDown className={`w-3 h-3 ${sortColumn === 'expertName' && sortDirection === 'desc' ? 'text-[#f15d18]' : 'text-gray-500'}`} />   </button>
                                      </span>
                                  </div>
                  </th>
              
                 
                {activeTab === "package" && (
                  <>
                    <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Created
                    </th>
                    <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Last Sale
                    </th>
                  </>
                )}
                {activeTab === "sales" && (
                  <>
                    <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Customer Name
                    </th>
                    <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Organization Name
                    </th>
                    <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Purchased
                    </th>
                  </>
                )}
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                  Ratings
                </th>
                {activeTab === "package" && (
                  <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                    
                     <div className="flex items-center gap-2">
                
                                  <span>
                               #Sales
                                  </span>
                                  <span className="col-span-1 flex flex-col">
                
                                      <button onClick={() => handleSort('sales', 'asc')}>
                                        <FaSortUp className={`w-3 h-3 ${sortColumn === 'sales' && sortDirection === 'asc' ? 'text-[#f15d18]' : 'text-gray-500'}`} />
                                      </button>
                                      <button onClick={() => handleSort('sales', 'desc')}>
                                        <FaSortDown className={`w-3 h-3 ${sortColumn === 'sales' && sortDirection === 'desc' ? 'text-[#f15d18]' : 'text-gray-500'}`} />   </button>
                                      </span>
                                  </div>
                  </th>
                )}
                {activeTab === "sales" && (
                  <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                    Reviews
                  </th>
                )}
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                 
                   <div className="flex items-center gap-2">
                
                                  <span>
                                  Earnings
                                  </span>
                                  <span className="col-span-1 flex flex-col">
                
                                      <button onClick={() => handleSort('earnings', 'asc')}>
                                        <FaSortUp className={`w-3 h-3 ${sortColumn === 'earnings' && sortDirection === 'asc' ? 'text-[#f15d18]' : 'text-gray-500'}`} />
                                      </button>
                                      <button onClick={() => handleSort('earnings', 'desc')}>
                                        <FaSortDown className={`w-3 h-3 ${sortColumn === 'earnings' && sortDirection === 'desc' ? 'text-[#f15d18]' : 'text-gray-500'}`} />   </button>
                                      </span>
                                  </div>
                </th>
                  {activeTab === "package" && (
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                  Action
                </th>)}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {error ? (
                <tr>
                  <td colSpan={activeTab === "package" ? 11 : 10} className="px-2 py-3 text-center">
                    <div className="text-red-500">Error loading data: {error}</div>
                  </td>
                </tr>
              ) : fetchingPackages || fetchingSales ? (
                <tr>
                  <td colSpan={activeTab === "package" ? 11 : 10} className="px-2 py-3 text-center">
                    <div className="text-gray-500">Loading...</div>
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((item: TooltipPackage | TooltipSalesData, index) => (
                  <tr key={index} className="bg-white">
                      {activeTab === "package" && (
                    <td className="px-2 py-3 whitespace-nowrap">
                     <div className="relative inline-block group max-w-[160px]">
<span>

                      <label className="relative group inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={(item as TooltipPackage).admindisabled ? false : true}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            const action = isChecked ? 'enable' : 'disable';
                            setStatusToggleModal({ open: true, action, package: item as TooltipPackage });
                          }}
                        />
                        <div className="w-11 h-6 bg-gray-200 rounded-full  peer-focus:ring-orange-300 peer-checked:bg-orange-500 peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  
                   </label>
</span>

                    {/* Tooltip on hover */}
                <div className="absolute left-0  -translate-y-1/2 -top-4 px-2 py-1 w-fit bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-30">
                 <div className="text-start">
                   {(item as TooltipPackage).admindisabled ? (
                     <>
                       <div className="nunito-regular text-[12px] text-[#696969] ">
                         Enable package visibility
                       </div>
                     </>
                   ) : (
                     <>
                       <div className="nunito-regular text-[12px] text-[#696969] ">
                         Disable package visibility
                       </div>
                     </>
                   )}
                 </div>
                </div> 
                      </div>

                    </td>)}
                    <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                        <div className="relative inline-block group max-w-[160px]">
 <span className="block truncate text-[14px] nunito-semibold  text-[#696969]" aria-label={item.packageId || ''}>

                       {((item .packageId || '').toString().length > 5)
                            ? `${(item.packageId || '').toString().slice(0, 5)}...`
                            : item.packageId}
                            </span>
                          {/* Styled hover tooltip showing full packageId */}
                        <div className="absolute left-0 -translate-y-1/2 -top-4 px-2 py-1 min-w-max bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-30 break-words">
                          {item.packageId}
                        </div>
                        </div>
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap">
                      <div className="text-[14px] truncate max-w-28 nunito-semibold text-[#696969]">
                        {item.packageName}
                      </div>
                    </td>
                       <td className="px-2 py-3 whitespace-nowrap ">
                      <div className="relative inline-block group max-w-[160px]">

                      <div className="text-[14px] truncate max-w-36 nunito-semibold text-[#696969]">
                        {item.primaryUrl}
                      </div>
                            {/* Styled hover tooltip showing full packageId */}
                        <div className="absolute left-0 -translate-y-1/2 -top-4 px-2 py-1 min-w-max bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-30 break-words">
                          {item.primaryUrl}
                        </div>
                      </div>

                    </td>
                    <td className="px-2 py-3 whitespace-nowrap">
                      <div className="text-[14px] truncate max-w-28 nunito-semibold text-[#696969]">
                        {(item as TooltipPackage).expertName }
                      </div>
                    </td>
                    
                    {activeTab === "package" && (
                      <>
                        <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                          {(item as TooltipPackage).created}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                          {(item as TooltipPackage).lastSale}
                        </td>
                      </>
                    )}
                    {activeTab === "sales" && (
                      <>
                        <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                          {(item as TooltipSalesData).customerName}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                          {(item as TooltipSalesData).orgName}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                          {(item as TooltipSalesData).purchased}
                        </td>
                      </>
                    )}
                    <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                      <div className="flex items-center">
                        {item.ratings} <span className="  ml-1"><Star className="text-[#F5A038] fill-[#F5A038] w-[24px] h-[24px]"/></span>
                      </div>
                    </td>
                    {activeTab === "package" && (

                      <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                        {(item as TooltipPackage).sales}
                      </td>   
                    )}
                    {activeTab === "sales" && (
                      <div className="group relative">
                      
                      <td className="px-2 py-3 whitespace-nowrap max-w-28 truncate nunito-regular text-[14px] text-[#696969]">
                        {(item as TooltipSalesData).reviews}
                      </td>{/* Tooltip on hover */}
                <div className="absolute left-0 -translate-y-1/2 -top-4 px-2 py-1 w-[174px] bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-30">
      <div className="text-start break-words whitespace-pre-line">
        <div className="nunito-regular text-[12px] text-[#696969] break-words whitespace-pre-line">
          {(item as TooltipSalesData).reviews}
                      </div>
                      </div>
        </div>
      </div>
                    )}
                    <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                      ${item.earnings}
                    </td>
                      {activeTab === "package" && (
                    <td className="px-2 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                   
                        <button className="text-orange-500 underline text-[14px] nunito-semibold">
                          View
                        </button>
                      </div>
                    </td>)}
                  </tr>
                ))
              ) : (
                // Check if search or filters are active
                searchQuery.trim() !== "" ||
                (appliedFilters && (
                  appliedFilters.createdDateRange ||
                  appliedFilters.lastSaleDateRange ||
                  appliedFilters.packageTooltipRating ||
                  appliedFilters.salesRanges ||
                  appliedFilters.earningsRanges
                )) ||
                (activeTab === "sales" && (
                  selectedOrganizations.length > 0 ||
                  dateFrom ||
                  dateTo ||
                  selectedRating ||
                  selectedPackages.length > 0
                )) ? (
                  <tr>
                    <td colSpan={activeTab === "package" ? 11 : 10}>
                      <div className="flex flex-col items-center py-20">
                        <div className="w-24 h-24 mb-4 flex items-center justify-center">
                          <Search className="w-16 h-16 text-gray-400" />
                        </div>
                        <h2 className="text-[24px] nunito-semibold text-[#404040] mb-2">
                          Oops! No matching records found.
                        </h2>
                        <p className="text-[#919191] text-[14px] nunito-semibold mb-4 text-center">
                          Try changing the filters or search criteria to see more
                          results!
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={activeTab === "package" ? 11 : 10}>
                       <div className="flex flex-col items-center mt-[70px]">
                        <div className="w-[416px] h-[214px] mb-4 flex items-center justify-center">
  <img src={tooltipDataNotFoundImg} alt="data not found"/>                        </div>
                        <h2 className="text-[24px] nunito-semibold mt-2 text-[#404040] ">
                         Oops! Nothing here yet.
                        </h2>
                       
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>

        {/* Add Pagination */}
        {currentItems?.length > 0 && totalPages > 1 && (
          <div className="py-3 flex items-center justify-center">
            <nav className="flex space-x-1">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-2 py-1 text-[#696969] ${
                    currentPage === index + 1
                      ? "bg-[#FAFAFA] border-[#EBEBEB] rounded-[8px] border"
                      : ""
                  }`}
                >
                  {String(index + 1).padStart(2, "0")}
                </button>
              ))}
            </nav>
          </div>
        )}

    
     </div>
     <DeleteModelSimple
       open={statusToggleModal.open}
       onDelete={handleStatusToggleConfirm}
       onCancel={handleStatusToggleCancel}
       message={
         statusToggleModal.action === 'enable'
           ? "Enabling this tooltip will make it visible again for new customer purchases. The expert owner will regain control to enable or disable it from their end.\nDo you want to proceed?"
           : "Disabling this tooltip will hide it from new customer purchases. The expert owner will not be able to re-enable it once disabled.\nAre you sure you want to proceed?"
       }
       cancelText="Cancel"
       deleteText={statusToggleModal.action === 'enable' ? "Yes, Enable" : "Yes, Disable"}
       loading={statusToggleLoading}
     />
    </Layout>
  );
};

export default Tooltip;
